import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { getLocaleFromPathname } from "../../constants/paths";
import {
  absoluteUrl,
  defaultOgImageAbsoluteUrl,
  getSiteUrl,
} from "../../constants/site";

function normalizePathname(pathname) {
  if (!pathname) return "/";
  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

function getBarePath(pathname) {
  const p = normalizePathname(pathname);
  if (p === "/en") return "/";
  if (p.startsWith("/en/")) return p.slice(3) || "/";
  return p;
}

export default function Meta({
  title,
  description,
  image,
  url,
  structuredData,
  keywords,
  ogType = "website",
  noindex = false,
}) {
  const { pathname } = useLocation();
  const siteUrl = getSiteUrl();

  const fullPath = normalizePathname(pathname);
  const canonical = url || `${siteUrl}${fullPath}`;

  const bare = getBarePath(fullPath);
  const deUrl = `${siteUrl}${bare === "/" ? "/" : bare}`;
  const enUrl = `${siteUrl}${bare === "/" ? "/en" : `/en${bare}`}`;

  const locale = getLocaleFromPathname(fullPath);
  const ogLocale = locale === "en" ? "en_US" : "de_DE";
  const ogAltLocale = locale === "en" ? "de_DE" : "en_US";

  const safeTitle =
    typeof title === "string" ? title.trim() : String(title || "");
  const safeDescription =
    typeof description === "string" ? description.trim() : "";

  const hasTitle = Boolean(safeTitle);
  const hasDescription = Boolean(safeDescription);
  const resolvedImage =
    image != null && String(image).trim()
      ? absoluteUrl(image)
      : "";
  const ogImage = resolvedImage || defaultOgImageAbsoluteUrl();

  return (
    <Helmet htmlAttributes={{ lang: locale === "en" ? "en" : "de" }}>
      {hasTitle ? <title>{safeTitle}</title> : null}
      {hasDescription ? (
        <meta name="description" content={safeDescription} />
      ) : null}
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={canonical} />

      <link rel="alternate" hrefLang="de" href={deUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={deUrl} />

      {noindex ? <meta name="robots" content="noindex,nofollow" /> : null}

      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      {hasTitle ? <meta property="og:title" content={safeTitle} /> : null}
      {hasDescription ? (
        <meta property="og:description" content={safeDescription} />
      ) : null}
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogAltLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      {hasTitle ? <meta name="twitter:title" content={safeTitle} /> : null}
      {hasDescription ? (
        <meta name="twitter:description" content={safeDescription} />
      ) : null}
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}

      {structuredData ? (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      ) : null}
    </Helmet>
  );
}

